import React, { useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import './ResumeSubPage.css';
import { getStoredToken } from '../../services/authService';
import { fetchProfileResumeAsFile } from '../../services/profileService';
import { evaluateResume, exportJdEvaluationToPdf, MAX_FILE_SIZE_MB } from '../../services/resumeService';
import EvaluationUsageView from '../../components/Resume/EvaluationUsageView';
import ResumeProfileResumeSource from './ResumeProfileResumeSource';
import { SAMPLE_CONTRACTOR_EVALUATION } from './sampleContractorEvaluation';

const ACCEPT = '.pdf,.docx';
const MAX_BYTES = 1024 * 1024; // 1MB

const statusClass = (status) => {
  if (!status) return '';
  const s = status.toUpperCase();
  if (s === 'PASS') return 'screening-status--pass';
  if (s === 'NEEDS FIX' || s === 'FLAGGED') return 'screening-status--warn';
  if (s === 'FAIL') return 'screening-status--fail';
  return '';
};

function ScreeningResultView({ data }) {
  const pick = (camel, snake) => (data?.[camel] != null ? data[camel] : data?.[snake]);
  const rec = pick('overallRecommendation', 'overall_recommendation') || '';
  const recClass =
    rec === 'APPROVE'
      ? 'screening-recommendation--approve'
      : rec === 'NEEDS REVISION'
        ? 'screening-recommendation--revision'
        : rec === 'REJECT'
          ? 'screening-recommendation--reject'
          : '';

  return (
    <div className="screening-result">
      <div className="screening-result__header">
        {data.candidate && (
          <p className="screening-result__candidate">Candidate: <strong>{data.candidate}</strong></p>
        )}
        {rec && (
          <p className="screening-result__recommendation">
            Overall: <span className={`screening-recommendation ${recClass}`}>{rec}</span>
          </p>
        )}
      </div>
      {data.sections && data.sections.length > 0 && (
        <div className="screening-result__sections">
          {data.sections.map((section) => (
            <div key={section.id ?? section.title} className="screening-section">
              <div className="screening-section__head">
                <span className="screening-section__title">
                  {section.id != null && `${section.id}. `}{section.title}
                </span>
                {section.status && (
                  <span className={`screening-section__status screening-status ${statusClass(section.status)}`}>
                    {section.status}
                  </span>
                )}
              </div>
              {section.notes && (
                <p className="screening-section__notes">{section.notes}</p>
              )}
              {section.issues && section.issues.length > 0 && (
                <ul className="screening-section__issues">
                  {section.issues.map((issue, idx) => (
                    <li key={idx} className="screening-issue">
                      <div className="screening-issue__problem">{issue.problem}</div>
                      {issue.suggestion && (
                        <div className="screening-issue__suggestion-row">
                          <span className="screening-issue__suggestion-label">Suggestion:</span>
                          <span className="screening-issue__suggestion">{issue.suggestion}</span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const EvaluateContractorResume = () => {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [screeningResult, setScreeningResult] = useState(null);
  const [evaluationUsage, setEvaluationUsage] = useState(null);
  const [error, setError] = useState(null);
  const { setRateLimitError } = useOutletContext() || {};
  const [pdfExportLoading, setPdfExportLoading] = useState(false);
  /** Logged-in: pick (choose path) → profile | upload. Anonymous: stays 'upload'. */
  const [resumeSource, setResumeSource] = useState(() =>
    getStoredToken() ? 'pick' : 'upload'
  );
  const [profileResumeLoading, setProfileResumeLoading] = useState(false);
  const inputRef = useRef(null);

  const clearState = () => {
    setError(null);
    setRateLimitError?.(null);
    setEvaluation(null);
    setScreeningResult(null);
    setEvaluationUsage(null);
    setPdfExportLoading(false);
  };

  const clearEvaluationResult = () => {
    setEvaluation(null);
    setScreeningResult(null);
    setEvaluationUsage(null);
  };

  /** Raw JSON string for PDF export (structured layout matches on-screen screening view). */
  const evaluationExportPayload = () => {
    if (screeningResult && typeof screeningResult === 'object') {
      try {
        return JSON.stringify(screeningResult);
      } catch {
        return '';
      }
    }
    if (typeof evaluation === 'string' && evaluation.trim().startsWith('{')) {
      try {
        const o = JSON.parse(evaluation);
        if (o && typeof o === 'object' && (Array.isArray(o.sections) || o.overallRecommendation)) {
          return JSON.stringify(o);
        }
      } catch {
        /* use raw string below */
      }
    }
    return typeof evaluation === 'string' ? evaluation : '';
  };

  const onExportPdf = async () => {
    const payload = evaluationExportPayload();
    if (!payload.trim() || pdfExportLoading) return;
    setPdfExportLoading(true);
    try {
      const { blob, filename } = await exportJdEvaluationToPdf(payload.trim());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'PDF export failed.');
    } finally {
      setPdfExportLoading(false);
    }
  };

  const validateFile = (f) => {
    if (!f) return null;
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    const validExt = /\.(pdf|docx)$/i.test(f.name);
    if (!validExt && !validTypes.includes(f.type)) {
      return 'Please upload a PDF or DOCX file.';
    }
    if (f.size > MAX_BYTES) {
      return `File must be under ${MAX_FILE_SIZE_MB}MB.`;
    }
    return null;
  };

  const handleFile = (selectedFile) => {
    clearState();
    if (getStoredToken()) setResumeSource('upload');
    if (!selectedFile) {
      setFile(null);
      return;
    }
    const err = validateFile(selectedFile);
    if (err) {
      setError(err);
      setFile(null);
      return;
    }
    setFile(selectedFile);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files[0];
    handleFile(f || null);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = () => {
    setDragActive(false);
  };

  const onInputChange = (e) => {
    const f = e.target.files[0];
    handleFile(f || null);
    e.target.value = '';
  };

  const onUploadClick = () => {
    inputRef.current?.click();
  };

  const onClearFile = (e) => {
    e.stopPropagation();
    if (loading) return;
    setFile(null);
    if (getStoredToken()) setResumeSource('upload');
    clearState();
    if (inputRef.current) inputRef.current.value = '';
  };

  const onPickUploadResume = () => {
    setResumeSource('upload');
  };

  const onBackToResumeChoices = () => {
    setResumeSource('pick');
    setFile(null);
    clearState();
    if (inputRef.current) inputRef.current.value = '';
  };

  const onPickProfileResume = async () => {
    setProfileResumeLoading(true);
    setError(null);
    try {
      const f = await fetchProfileResumeAsFile();
      if (!f) {
        setError('No resume in your profile. Add one under Profile.');
        return;
      }
      const err = validateFile(f);
      if (err) {
        setError(err);
        return;
      }
      clearState();
      setFile(f);
      setResumeSource('profile');
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Could not load profile resume.');
    } finally {
      setProfileResumeLoading(false);
    }
  };

  const onChooseFileUploadInstead = () => {
    setResumeSource('upload');
    setFile(null);
    clearState();
    if (inputRef.current) inputRef.current.value = '';
  };

  // Set to true to use hardcoded sample for Evaluate only (for frontend preview)
  const USE_SAMPLE_EVALUATION = false;

  const onSubmit = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setRateLimitError?.(null);
    try {
      if (USE_SAMPLE_EVALUATION) {
        await new Promise((r) => setTimeout(r, 800));
        setEvaluation(SAMPLE_CONTRACTOR_EVALUATION);
      } else {
        const result = await evaluateResume(file, 'resume-screening-contractor-evaluate');
        if (result.success) {
          setEvaluation(result.evaluation);
          setScreeningResult(result.screeningResult || null);
          setEvaluationUsage(result.usage || null);
        } else {
          clearEvaluationResult();
          const message = result.error || 'Evaluation failed.';
          if (result.rateLimitExceeded) {
            setRateLimitError?.(message);
          } else {
            setError(message);
          }
        }
      }
    } catch (err) {
      clearEvaluationResult();
      const message = err.message || 'Something went wrong.';
      if (/limit reached/i.test(message)) {
        setRateLimitError?.(message);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const userLoggedIn = !!getStoredToken();
  const resumeSourceEffective = userLoggedIn ? resumeSource : 'upload';
  const showManualUploadZone = resumeSourceEffective === 'upload';

  return (
    <div className="resume-subpage">
      <h2>Evaluate Contractor Resume</h2>

      <ResumeProfileResumeSource
        loggedIn={userLoggedIn}
        resumeSource={resumeSourceEffective}
        profileResumeLoading={profileResumeLoading}
        fileName={file?.name}
        disableActions={loading}
        onPickProfileResume={onPickProfileResume}
        onPickUploadResume={onPickUploadResume}
        onChooseFileUploadInstead={onChooseFileUploadInstead}
        onBackToResumeChoices={onBackToResumeChoices}
      />

      {showManualUploadZone && (
        <div
          className={`resume-upload-zone ${dragActive ? 'resume-upload-zone--drag' : ''} ${error ? 'resume-upload-zone--error' : ''}`}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={onUploadClick}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={onInputChange}
            className="resume-upload-zone__input"
            disabled={loading}
          />
          {file ? (
            <div className="resume-upload-zone__file">
              <span className="resume-upload-zone__file-name">{file.name}</span>
              <span className="resume-upload-zone__file-size">
                ({(file.size / 1024).toFixed(1)} KB)
              </span>
              <button
                type="button"
                className="resume-upload-zone__clear"
                onClick={onClearFile}
                disabled={loading}
                aria-label="Remove file"
                title="Remove and re-upload"
              >
                ×
              </button>
            </div>
          ) : (
            <p className="resume-upload-zone__hint">
              Drop your resume here, or click to browse. Max {MAX_FILE_SIZE_MB}MB · PDF or DOCX
            </p>
          )}
        </div>
      )}

      {error && <div className="resume-subpage__error">{error}</div>}

      <div className="resume-subpage__actions">
        <button
          type="button"
          className="resume-subpage__submit"
          onClick={onSubmit}
          disabled={!file || loading}
        >
          {loading ? 'Evaluating…' : 'Evaluate'}
        </button>
        <button
          type="button"
          className="resume-subpage__submit"
          onClick={onExportPdf}
          disabled={!evaluationExportPayload().trim() || loading || pdfExportLoading}
        >
          {pdfExportLoading ? 'Preparing PDF…' : 'Download Report'}
        </button>
      </div>

      {evaluation && (
        <div className="resume-subpage__result">
          <h3>Evaluation</h3>
          {screeningResult ? (
            <ScreeningResultView data={screeningResult} />
          ) : (
            <div className="resume-subpage__result-content">{evaluation}</div>
          )}
          <EvaluationUsageView usage={evaluationUsage} />
        </div>
      )}
    </div>
  );
};

export default EvaluateContractorResume;
